import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'
import { supabase } from '../lib/supabase'
import ReceiptUploadModal from '../components/ReceiptUploadModal'
import ReceiptDetailModal from '../components/ReceiptDetailModal'
import { useSubscription } from '../lib/useSubscription'
import { openCheckout } from '../lib/paddle'

const teal = '#0D9488'
const tealLight = '#14B8A6'
const plum = '#1E1040'

const tagAccents = {
  schedule_c: teal,
  company_card: '#2563EB',
  reimbursable: '#F59E0B',
  personal: '#A855F7',
}

// Sentinel tab for receipts saved without a tag (legacy orphans). Lets users
// see and fix them — they'd otherwise never match a real tag tab and stay hidden.
const UNTAGGED = '__untagged__'
const untaggedAccent = '#B45309'

export default function Receipts() {
  const { t } = useTranslation()
  const { canWrite } = useSubscription()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [receipts, setReceipts] = useState([])
  const [categories, setCategories] = useState([])
  const [tags, setTags] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [search, setSearch] = useState('')
  const [monthFilter, setMonthFilter] = useState('current')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [uploadModal, setUploadModal] = useState(null)
  const [detailModal, setDetailModal] = useState(null)

  const [activeTagId, setActiveTagId] = useState(searchParams.get('tag') || null)

  const switchTag = (tagId) => {
    setActiveTagId(tagId)
    setCategoryFilter('all')
    // Orphans can be from any month — show all time so the count badge always matches the list
    if (tagId === UNTAGGED) setMonthFilter('all')
    if (tagId) {
      searchParams.set('tag', tagId)
    } else {
      searchParams.delete('tag')
    }
    setSearchParams(searchParams, { replace: true })
  }

  useEffect(() => {
    loadAll()
    const addParam = searchParams.get('add')
    if (['scan', 'upload', 'pdf', 'manual', 'picker'].includes(addParam)) {
      setUploadModal(addParam === 'pdf' ? 'upload' : addParam)
      searchParams.delete('add')
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    const [
      { data: tagsData },
      { data: catsData },
      { data: pmData },
      { data: txData },
    ] = await Promise.all([
      supabase.from('tags').select('*').eq('user_id', user.id).order('sort_order'),
      supabase.from('categories').select('id, name, icon, category_type').eq('user_id', user.id).order('sort_order'),
      supabase.from('payment_methods').select('*').eq('user_id', user.id).order('sort_order'),
      supabase.from('transactions').select('*').eq('user_id', user.id).order('transaction_date', { ascending: false }),
    ])

    setTags(tagsData || [])
    setCategories(catsData || [])
    setPaymentMethods(pmData || [])
    setReceipts(txData || [])

    if (!activeTagId && tagsData && tagsData.length > 0) {
      const firstTag = tagsData[0].id
      setActiveTagId(firstTag)
    }

    setLoading(false)
  }

  const handleSaved = (row) => {
    setReceipts(prev => [row, ...prev])
  }

  const handleUpdated = (updated) => {
    setReceipts(prev => prev.map(r => r.id === updated.id ? updated : r))
    setDetailModal(updated)
  }

  const handleDeleted = (id) => {
    setReceipts(prev => prev.filter(r => r.id !== id))
  }

  const activeTag = tags.find(tg => tg.id === activeTagId)

  const now = new Date()
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
  const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)

  const untaggedCount = receipts.filter(r => !r.tag_id).length

  const filtered = receipts.filter(r => {
    if (activeTagId === UNTAGGED) {
      if (r.tag_id) return false
    } else if (activeTagId && r.tag_id !== activeTagId) {
      return false
    }
    if (monthFilter === 'current') {
      if (r.transaction_date < currentMonthStart || r.transaction_date > currentMonthEnd) return false
    } else if (monthFilter !== 'all') {
      if (!r.transaction_date.startsWith(monthFilter)) return false
    }
    if (categoryFilter !== 'all' && r.category_id !== categoryFilter) return false
    if (search) {
      const q = search.toLowerCase()
      const m = (r.merchant || '').toLowerCase()
      const memo = (r.memo || '').toLowerCase()
      if (!m.includes(q) && !memo.includes(q)) return false
    }
    return true
  })

  const receiptsInActiveTag = receipts.filter(r =>
    activeTagId === UNTAGGED ? !r.tag_id : (!activeTagId || r.tag_id === activeTagId)
  )
  const monthsSet = new Set()
  receiptsInActiveTag.forEach(r => { if (r.transaction_date) monthsSet.add(r.transaction_date.slice(0, 7)) })
  const months = Array.from(monthsSet).sort().reverse()

  const filteredTotal = filtered.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0)

  // Locale-aware formatters
  const lang = i18n.language || 'en'
  const locale = lang.startsWith('es') ? 'es-US' : 'en-US'
  const formatCurrency = (n) => new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n || 0)
  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  const formatMonth = (ym) => new Date(ym + '-01T00:00:00').toLocaleDateString(locale, { month: 'long', year: 'numeric' })

  const getCategoryById = (id) => categories.find(c => c.id === id)
  const getPaymentMethodById = (id) => paymentMethods.find(p => p.id === id)

  const tagSubtitleKey = activeTag ? `receipts.subtitle_${activeTag.tag_type}` : 'receipts.subtitle_default'

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>{t('receipts.title')}</h1>
          <p style={styles.subtitle}>
            {activeTagId === UNTAGGED
              ? t('receipts.subtitle_untagged')
              : activeTag ? t(tagSubtitleKey, t('receipts.subtitle_default')) : t('receipts.subtitle_all')}
          </p>
        </div>
        <button
          style={{ ...styles.addBtn, opacity: canWrite ? 1 : 0.55 }}
          onClick={async () => {
            if (!canWrite) {
              const { data: { user } } = await supabase.auth.getUser()
              if (user) {
                await openCheckout({
                  userEmail: user.email,
                  userId: user.id,
                  onSuccess: () => setTimeout(() => window.location.reload(), 2500),
                })
              }
              return
            }
            setUploadModal('picker')
          }}
        >
          {!canWrite ? (
            <>🔒 {t('receipts.subscribe_to_add')}</>
          ) : activeTag ? (
            <>+ {t('receipts.add_to_tag', { icon: activeTag.icon, name: activeTag.name })}</>
          ) : (
            <>+ {t('receipts.add_receipt')}</>
          )}
        </button>
      </div>

      {/* Tag tabs */}
      {tags.length > 0 && (
        <div style={styles.tagRow}>
          <div style={styles.tagScroll}>
            {tags.map(tg => {
              const isActive = tg.id === activeTagId
              const accent = tagAccents[tg.tag_type] || teal
              return (
                <button
                  key={tg.id}
                  style={{
                    ...styles.tagPill,
                    ...(isActive ? { background: '#fff', color: plum, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderColor: accent } : {}),
                  }}
                  onClick={() => switchTag(tg.id)}
                >
                  <span style={{ fontSize: '16px' }}>{tg.icon}</span>
                  {tg.name}
                </button>
              )
            })}
            {/* Untagged tab — only when orphan (NULL-tag) receipts exist, so users can find and fix them */}
            {untaggedCount > 0 && (
              <button
                style={{
                  ...styles.tagPill,
                  ...(activeTagId === UNTAGGED ? { background: '#fff', color: plum, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderColor: untaggedAccent } : {}),
                }}
                onClick={() => switchTag(UNTAGGED)}
              >
                <span style={{ fontSize: '16px' }}>⚠️</span>
                {t('receipts.untagged_tab')} ({untaggedCount})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Filters bar */}
      <div style={styles.filtersBar}>
        <div style={styles.searchField}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder={t('receipts.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
        <select
          style={styles.filterSelect}
          value={monthFilter}
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          <option value="current">{t('receipts.this_month')}</option>
          <option value="all">{t('receipts.all_time')}</option>
          {months.map(m => (
            <option key={m} value={m}>{formatMonth(m)}</option>
          ))}
        </select>
        <select
          style={styles.filterSelect}
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="all">{t('receipts.all_categories')}</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
          ))}
        </select>
      </div>

      {/* Totals strip */}
      <div style={styles.totalsStrip}>
        <div style={styles.totalsLabel}>
          {t('receipts.count_label', { count: filtered.length })}
          {monthFilter === 'current' && ` ${t('receipts.this_month_lower')}`}
          {monthFilter !== 'all' && monthFilter !== 'current' && ` ${t('receipts.in_month', { month: formatMonth(monthFilter) })}`}
          {activeTag && ` · ${activeTag.name.toLowerCase()}`}
        </div>
        <div style={styles.totalsValue}>{formatCurrency(filteredTotal)}</div>
      </div>

      {/* List */}
      {loading ? (
        <div style={styles.loading}>{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          hasAny={receipts.length > 0}
          activeTag={activeTag}
          onAdd={() => setUploadModal('picker')}
          t={t}
        />
      ) : (
        <div style={styles.list}>
          {filtered.map(r => {
            const cat = getCategoryById(r.category_id)
            const pm = getPaymentMethodById(r.payment_method_id)
            return (
              <button key={r.id} style={styles.row} onClick={() => setDetailModal(r)}>
                <div style={styles.rowIconBox}>
                  {r.receipt_url ? (
                    r.receipt_file_type === 'pdf' ? (
                      <div style={styles.pdfBox}>📄</div>
                    ) : (
                      <img src={r.receipt_url} alt="" style={styles.thumb} />
                    )
                  ) : (
                    <div style={styles.manualBox}>📝</div>
                  )}
                </div>
                <div style={styles.rowMain}>
                  <div style={styles.rowMerchant}>{r.merchant || t('receipts.unknown_merchant')}</div>
                  <div style={styles.rowMeta}>
                    <span>{formatDate(r.transaction_date)}</span>
                    {!r.tag_id && <span style={styles.rowNoTag}>⚠️ {t('receipts.no_tag_badge')}</span>}
                    {cat && <span style={styles.rowCat}>{cat.icon} {cat.name}</span>}
                    {pm && <span style={styles.rowPm}>{pm.label}{pm.last_4 ? ` ••${pm.last_4}` : ''}</span>}
                    {r.ai_extracted && <span style={styles.rowAi}>✨ {t('receipts.ai_badge')}</span>}
                  </div>
                </div>
                <div style={styles.rowAmount}>{formatCurrency(r.amount)}</div>
              </button>
            )
          })}
        </div>
      )}

      {/* Modals */}
      {uploadModal && (
        <ReceiptUploadModal
          initialMode={uploadModal === 'picker' ? null : uploadModal}
          defaultTagId={activeTagId === UNTAGGED ? null : activeTagId}
          tags={tags}
          categories={categories}
          paymentMethods={paymentMethods}
          onClose={() => {
            setUploadModal(null)
            loadAll()
          }}
          onSaved={handleSaved}
        />
      )}
      {detailModal && (
        <ReceiptDetailModal
          receipt={detailModal}
          tags={tags}
          categories={categories}
          paymentMethods={paymentMethods}
          startInEdit={!detailModal.tag_id}
          highlightTag={!detailModal.tag_id}
          onClose={() => {
            setDetailModal(null)
            loadAll()
          }}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}

function EmptyState({ hasAny, activeTag, onAdd, t }) {
  const iconMap = {
    schedule_c: '💼',
    company_card: '🏢',
    reimbursable: '💵',
    personal: '🏠',
  }
  const icon = activeTag ? iconMap[activeTag.tag_type] || '🧾' : '🧾'
  const tagName = activeTag?.name?.toLowerCase() || t('receipts.tag_default_label')

  const emptyTitle = hasAny
    ? t('receipts.empty_filters_title')
    : activeTag ? t('receipts.empty_tag_title', { tag: tagName }) : t('receipts.empty_title')

  const emptySub = hasAny
    ? t('receipts.empty_filters_sub')
    : activeTag
      ? t('receipts.empty_tag_sub')
      : t('receipts.empty_sub')

  return (
    <div style={styles.empty}>
      <div style={styles.emptyIcon}>{icon}</div>
      <div style={styles.emptyTitle}>{emptyTitle}</div>
      <div style={styles.emptySub}>{emptySub}</div>
      {!hasAny && (
        <button style={styles.emptyBtn} onClick={onAdd}>
          + {t('receipts.add_first')}
        </button>
      )}
    </div>
  )
}

const styles = {
  page: { padding: '32px 40px 80px', maxWidth: '1080px', margin: '0 auto', fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px', gap: '16px', flexWrap: 'wrap' },
  title: { fontFamily: "'Fraunces', serif", fontSize: '36px', fontWeight: 900, letterSpacing: '-1px', color: plum, margin: 0, lineHeight: 1.1 },
  subtitle: { fontSize: '14px', color: '#6B7280', marginTop: '6px', fontWeight: 500, maxWidth: '640px', lineHeight: 1.5 },
  addBtn: {
    background: teal, color: '#042f2e', border: 'none',
    padding: '11px 22px', borderRadius: '100px',
    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 4px 14px ${teal}40`, flexShrink: 0,
  },
  tagRow: { marginBottom: '16px', overflowX: 'auto' },
  tagScroll: {
    display: 'inline-flex', gap: '8px', padding: '4px',
    background: '#F3F4F6', borderRadius: '100px', flexWrap: 'nowrap',
  },
  tagPill: {
    padding: '9px 18px', background: 'transparent',
    border: '2px solid transparent', borderRadius: '100px',
    fontSize: '13px', fontWeight: 700, color: '#6B7280',
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    transition: 'all 0.15s', display: 'flex',
    alignItems: 'center', gap: '7px', whiteSpace: 'nowrap',
  },
  filtersBar: { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '10px', marginBottom: '14px' },
  searchField: { position: 'relative' },
  searchIcon: { position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: '#9CA3AF', pointerEvents: 'none' },
  searchInput: {
    width: '100%', padding: '11px 14px 11px 38px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px', background: '#fff',
    outline: 'none', fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: plum, boxSizing: 'border-box',
  },
  filterSelect: {
    padding: '11px 14px', borderRadius: '10px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    background: '#fff', outline: 'none',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: plum, cursor: 'pointer', fontWeight: 500,
  },
  totalsStrip: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px',
    background: `linear-gradient(135deg, ${teal}, ${tealLight})`,
    borderRadius: '14px', marginBottom: '16px', color: '#fff',
  },
  totalsLabel: { fontSize: '13px', fontWeight: 600, opacity: 0.95 },
  totalsValue: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, letterSpacing: '-0.3px' },
  loading: { padding: '60px 20px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' },
  list: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: '16px', overflow: 'hidden' },
  row: {
    display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '14px', padding: '14px 18px',
    borderTop: '1px solid #F3F4F6', alignItems: 'center', width: '100%', background: '#fff',
    border: 'none', cursor: 'pointer', textAlign: 'left',
    fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'background 0.15s',
  },
  rowIconBox: {
    width: '44px', height: '44px', borderRadius: '10px',
    background: '#F9FAFB', border: '1px solid #E5E7EB',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, overflow: 'hidden',
  },
  thumb: { width: '100%', height: '100%', objectFit: 'cover' },
  pdfBox: { fontSize: '22px' },
  manualBox: { fontSize: '22px' },
  rowMain: { minWidth: 0 },
  rowMerchant: { fontSize: '14px', fontWeight: 700, color: plum, marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMeta: { fontSize: '12px', color: '#9CA3AF', fontWeight: 500, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  rowCat: { color: '#6B7280' },
  rowPm: { color: '#6B7280' },
  rowNoTag: {
    fontSize: '10px', fontWeight: 700, color: untaggedAccent,
    background: 'rgba(180, 83, 9, 0.1)',
    padding: '2px 8px', borderRadius: '4px',
    letterSpacing: '0.02em', whiteSpace: 'nowrap',
  },
  rowAi: {
    fontSize: '10px', fontWeight: 700, color: teal,
    background: 'rgba(13, 148, 136, 0.1)',
    padding: '2px 8px', borderRadius: '4px',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  rowAmount: { fontFamily: "'Fraunces', serif", fontSize: '16px', fontWeight: 800, color: plum },
  empty: { background: '#fff', border: '1px dashed #D1D5DB', borderRadius: '16px', padding: '56px 32px', textAlign: 'center' },
  emptyIcon: { fontSize: '56px', marginBottom: '14px' },
  emptyTitle: { fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800, color: plum, marginBottom: '8px', letterSpacing: '-0.3px' },
  emptySub: { fontSize: '14px', color: '#6B7280', marginBottom: '22px', fontWeight: 500 },
  emptyBtn: {
    background: teal, color: '#042f2e', border: 'none',
    padding: '13px 28px', borderRadius: '100px',
    fontSize: '15px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    boxShadow: `0 6px 20px ${teal}40`,
  },
}
